import { fetchAPI, METHODS, parseBody, assertErrors } from './common';
import qs from 'qs';

/**
 * @typedef UserUpdate
 * @property {string} name
 * @property {string} phone
 * @property {number} lat
 * @property {number} lng
 * @property {number} pickup_dist
 */

/**
 * Updates the user entry.
 * Note: only the name & phone entry can be updated.
 * @param {UserUpdate} data
 */
export async function updateUser(data) {
  const res = await fetchAPI('/user', METHODS.put, data);
  if (res.status !== 200) throw new Error(res.statusText);
}

/**
 * @typedef Invite
 * @property {string} name
 * @property {string} team_id
 * @property {boolean} isNewUser
 */

export class NoInvitesError extends Error {
  constructor() {
    super('No invites found');
    this.name = 'NoInvitesError';
  }
}

/**
 * Fetched all outgoing invitations associated with `email`.
 * @returns Promise<Inivite[]>
 */
export async function getInvites(email, { emailProof, token } = {}) {
  // Without a session the API needs proof the caller owns `email`: the invite
  // token from the email link, or an email proof from `auth.verifyEmailCode`.
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  const res = await fetchAPI(
    `/user/pending-invites/${encodeURIComponent(email)}${query}`,
    METHODS.get,
    undefined,
    emailProof
      ? { headers: { 'x-email-proof': emailProof }, noAuthRefresh: true }
      : {}
  );
  if (res.status === 404) {
    throw new NoInvitesError();
  }
  await assertErrors(res);
  const invites = await parseBody(res, true);
  return invites;
}

export async function updateChatSettings(data) {
  const res = await fetchAPI('/user/business-chat-info', METHODS.put, data);
  return await parseBody(res);
}

/**
 * Accepts an invite. Requires a session for the invited email.
 * @param {string} teamId
 * @param {string} email
 */
export async function acceptInvite(teamId, email) {
  const res = await fetchAPI('/user/accept-team-invite', METHODS.put, {
    teamId,
    email,
  });
  await parseBody(res);
}

/**
 * Ignores an invite. Requires a session for the invited email.
 * @param {string} teamId
 * @param {string} email
 */
export async function ignoreInvite(teamId, email) {
  const res = await fetchAPI('/user/ignore-team-invite', METHODS.put, {
    teamId,
    email,
  });
  await parseBody(res);
}

/**
 * @typedef WeekTimesObj
 * See `src/utils/weekTimes.js` for utilities to generate this object
 */

/**
 * @typedef BusinessUpdate
 * @property {string} businessName
 * @property {string} businessPhone
 * @property {string} ein
 * @property {string} description
 * @property {string} businessEmail
 * @property {string} lang
 * @property {string} contactName
 * @property {string} contactPhone
 * @property {string} contactEmail
 * @property {object} acceptedFoodWeights
 * @property {WeekTimesObj} weekTimesObj
 */

/**
 * Updates the business entry.
 * @param {BusinessUpdate} data
 */
export async function updateBusiness(data) {
  const res = await fetchAPI('/user/business', METHODS.put, data);
  if (res.status !== 200) {
    const body = await res.json();
    throw new Error(body?.message ?? res.statusText);
  }
}

/**
 * Returns true if a user with the email `email` exists.
 * @param {string} email
 * @returns Promise<boolean>
 */
export async function checkUserExists(email) {
  const res = await fetchAPI(
    `/user/exists/${encodeURIComponent(email)}`,
    METHODS.get
  );
  const { exists } = await parseBody(res);
  return exists;
}

/**
 * Logins in a user and accepts an invite at the same time.
 * @param {string} email
 * @param {string} email
 * @param {string} teamId
 * @returns Promise<User>
 */
export async function acceptInviteAndLogin(email, password, teamId) {
  const res = await fetchAPI(
    '/user/accept-team-invite-and-login',
    METHODS.post,
    { email, password, teamId }
  );
  const { user } = await parseBody(res);
  return user;
}

/**
 * Reauthenticate under a new team.
 * @param {string} teamId
 * @param {string} refreshToken Note, this refresh token will be revoked
 * @returns Promise<User>
 */
export async function switchTeams(teamId, refreshToken) {
  const res = await fetchAPI(`/user/switch-teams/${teamId}`, METHODS.post, {
    refreshToken,
  });
  const { user } = await parseBody(res);
  return user;
}

/**
 * Get all teams the current signed in user is a part of.
 * @returns Promise<Team[]>
 */
export async function getTeams(args = {}) {
  const res = await fetchAPI(`/user/teams/?${qs.stringify(args)}`, METHODS.get);
  const { teams } = await parseBody(res);
  return teams;
}

/**
 * Manually set the status of a training program for a user.
 * @param {string} userId
 * @param {boolean} isDone
 */
export async function setTrainingStatus(userId, isDone) {
  await fetchAPI('/user/training-status', METHODS.post, { userId, isDone });
}

/**
 * Reset a user's progress on the training program
 * @param {string} userId
 */
export async function resetProgress(userId) {
  await fetchAPI('/user/reset-progress', METHODS.post, { userId });
}

/**
 * Fetch a user's notification settings for the current team.
 * @returns Promise<NotificationSettings>
 */
export async function getNotificationSettings() {
  const res = await fetchAPI('/user/notification-settings', METHODS.get);
  const { settings } = await parseBody(res);
  return settings;
}

/**
 * Update a user's notification settings for the current team.
 * @param {object} settings
 */
export async function updateNotificationSettings(settings) {
  await fetchAPI('/user/notification-settings', METHODS.post, settings);
}

/**
 * Unsubscribe from a specific type of notifications or all notifications.
 * This route does not require authorization.
 * @param {"trainingCompleted" | "rescueAvailability" | "foodRunAssigned" | "foodRunUpcoming" | "foodRunMissingWeight" | "foodRunCancelled" | "foodRunChanged" | "foodRunIncomplete" | "foodRunDriverUnassigned" | "distributionRouteAssigned" | "distributionRouteReminder" | "distributionRouteCancelled" | "distributionRouteEdited" | "distributionRouteClaimed" | "distributionReassignmentNeeded" | "distributionRoutesPosted" | "agreements" | "rescueAssign" | "rescuesIncomplete" | "marketplaceOrderReceived" | "marketplaceOrderConfirmed" | "marketplaceOrderCancelled" | "marketplacePaymentFailed" | "marketplaceListingFullySold" | "marketplaceConnectAccountActionNeeded" | "all"} type
 * @param {string} userId
 * @param {string} teamId
 * @returns Promise<void>
 */
export async function unsubscribe(type, userId, teamId) {
  const res = await fetchAPI(
    `/notification/${type}/${userId}/${teamId}`,
    METHODS.delete
  );
  if (res.status !== 200) {
    let error = new Error(res.statusText);
    try {
      const { message } = await res.json();
      error = new Error(message);
    } catch {
      // pass
    }

    throw error;
  }
}

/**
 * Start verifying the user's phone number.
 */
export async function startPhoneVerification() {
  const res = await fetchAPI('/user/verify-phone/start', METHODS.post);
  await assertErrors(res);
}

/**
 * Finish verifying the user's phone number, requires the sent code.
 * @param {string} code
 */
export async function completePhoneVerification(code) {
  const res = await fetchAPI('/user/verify-phone/complete', METHODS.post, {
    code,
  });
  await assertErrors(res);
}

/**
 * Skip phone verification permanently.
 */
export async function skipPhoneVerification() {
  const res = await fetchAPI('/user/verify-phone/skip', METHODS.post);
  await assertErrors(res);
}

/**
 * Check the status of the most recent phone verification message.
 * @returns {Promise<"UNKNOWN" | "UNREACHABLE" | "SENT">}
 */
export async function checkPhoneVerificationStatus() {
  const res = await fetchAPI('/user/verify-phone/check-status', METHODS.get);
  await assertErrors(res);
  const { status } = await parseBody(res);
  return status;
}

/**
 * Fetch user data by email
 */
export async function getByEmail(email) {
  const res = await fetchAPI(
    `/user/email/${encodeURIComponent(email)}`,
    METHODS.get
  );
  return await parseBody(res, true);
}

export async function updateUserLang(args) {
  const res = await fetchAPI('/user/update-lang', METHODS.put, args);
  await assertErrors(res);
  return await parseBody(res, true);
}

export async function updateStartLocation(args) {
  const res = await fetchAPI('/user/start-location', METHODS.put, args);
  await assertErrors(res);
  return await parseBody(res);
}

/**
 *
 * @param {object} param0
 * @param {"google" | "microsoft" | "apple"} param0.provider
 */
export async function connectCalendar({ provider, ...args }) {
  const res = await fetchAPI(
    `/user/connect-calendar/${provider}`,
    METHODS.post,
    args
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 *
 * @param {object} param0
 * @param {"google" | "microsoft" | "apple"} param0.provider
 */
export async function stopCalendarSync({ provider, ...args }) {
  const res = await fetchAPI(
    `/user/stop-calendar-sync/${provider}`,
    METHODS.post,
    args
  );
  await assertErrors(res);
  return await parseBody(res);
}

export async function reportUser(data) {
  const res = await fetchAPI(`/user/report`, METHODS.post, data);
  return await parseBody(res, true);
}

/**
 * Get the approval status for the current user's team
 * @returns {Promise<{isPendingApproval: boolean, queueEntry: object|null}>}
 */
export async function getApprovalStatus() {
  const res = await fetchAPI('/user/approval-status', METHODS.get);
  await assertErrors(res);
  const { data } = await parseBody(res);
  return data;
}

/**
 * Updates the user's first and last name.
 * @param {Object} params
 * @param {string} params.first_name - The user's first name.
 * @param {string} params.last_name - The user's last name.
 */
export async function updateUserName({ first_name, last_name }) {
  const data = { first_name, last_name };
  const res = await fetchAPI('/user/name', METHODS.put, data);
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Fetch per-user UI settings blob for a specific table key.
 * Returns the stored JSON object or null if nothing is saved yet.
 *
 * @param {string} tableKey
 * @returns {Promise<object | null>}
 */
export async function getInterfaceSettings(tableKey) {
  const res = await fetchAPI(
    `/user/interface-settings/${encodeURIComponent(tableKey)}`,
    METHODS.get
  );

  if (res.status === 204) {
    return null;
  }

  await assertErrors(res, true);
  const body = await parseBody(res, true);

  return body?.settingValue ?? null;
}

/**
 * Persist per-user UI settings blob for a specific table key.
 *
 * @param {string} tableKey
 * @param {object} settingValue
 * @returns {Promise<void>}
 */
export async function saveInterfaceSettings(tableKey, settingValue) {
  const res = await fetchAPI(
    `/user/interface-settings/${encodeURIComponent(tableKey)}`,
    METHODS.put,
    { settingValue }
  );
  await assertErrors(res);
}
